/**
 * Catálogo y Terminal de Mercado Global (+60,000 Tickers) de FinVoz.
 *
 * Carga diferida (lazy loading) por segmentos para mantener el rendimiento móvil
 * y el consumo de memoria en niveles óptimos. Búsqueda instantánea en cliente (0 ms).
 */

export type MarketSegment = "us" | "latam" | "etf" | "eu" | "asia";

export interface MarketInstrumentRecord {
  readonly s: string; // Símbolo / Ticker
  readonly n: string; // Nombre descriptivo
  readonly e: string; // Bolsa / Exchange (NASDAQ, NYSE, BVL, XETRA...)
  readonly t: "Stock" | "ETF"; // Tipo de activo
  readonly c: string; // Código de país (US, PE, GB...)
  readonly i?: string | undefined; // ISIN (opcional)
}

export interface MarketSegmentMeta {
  readonly id: MarketSegment;
  readonly label: string;
  readonly icon: string;
  readonly description: string;
  readonly estimatedCount: number;
}

export const MARKET_SEGMENTS: readonly MarketSegmentMeta[] = [
  {
    id: "us",
    label: "EE.UU.",
    icon: "🗽",
    description: "NASDAQ, NYSE, AMEX, OTC",
    estimatedCount: 17800,
  },
  {
    id: "latam",
    label: "Latam / BVL",
    icon: "🌎",
    description: "Bolsa de Valores de Lima, B3, BMV",
    estimatedCount: 2800,
  },
  {
    id: "etf",
    label: "ETFs Globales",
    icon: "🌐",
    description: "Vanguard, BlackRock iShares, SPDR",
    estimatedCount: 16100,
  },
  {
    id: "eu",
    label: "Europa",
    icon: "💶",
    description: "LSE, XETRA, Euronext, BME",
    estimatedCount: 32300,
  },
  {
    id: "asia",
    label: "Asia / Pacífico",
    icon: "⛩️",
    description: "HKEX, TSE, SSE, India, ASX",
    estimatedCount: 28500,
  },
];

// Fallback mínimo de emergencia en caso de modo offline estricto sin precarga previa
const FALLBACK_SEEDS: Record<MarketSegment, MarketInstrumentRecord[]> = {
  us: [
    { s: "AAPL", n: "Apple Inc.", e: "NASDAQ", t: "Stock", c: "US" },
    { s: "MSFT", n: "Microsoft Corporation", e: "NASDAQ", t: "Stock", c: "US" },
    { s: "AMZN", n: "Amazon.com Inc.", e: "NASDAQ", t: "Stock", c: "US" },
    { s: "GOOGL", n: "Alphabet Inc. Class A", e: "NASDAQ", t: "Stock", c: "US" },
    { s: "NVDA", n: "NVIDIA Corporation", e: "NASDAQ", t: "Stock", c: "US" },
    { s: "TSLA", n: "Tesla Inc.", e: "NASDAQ", t: "Stock", c: "US" },
    { s: "VOO", n: "Vanguard S&P 500 ETF", e: "NYSE ARCA", t: "ETF", c: "US" },
    { s: "SPY", n: "SPDR S&P 500 ETF Trust", e: "NYSE ARCA", t: "ETF", c: "US" },
    { s: "QQQ", n: "Invesco QQQ Trust", e: "NASDAQ", t: "ETF", c: "US" },
    { s: "MU", n: "Micron Technology Inc.", e: "NASDAQ", t: "Stock", c: "US" },
    { s: "META", n: "Meta Platforms Inc.", e: "NASDAQ", t: "Stock", c: "US" },
    { s: "AMD", n: "Advanced Micro Devices", e: "NASDAQ", t: "Stock", c: "US" },
  ],
  latam: [
    { s: "BAP", n: "Credicorp Ltd.", e: "NYSE", t: "Stock", c: "PE", i: "BMG2519Y1084" },
    { s: "SCCO", n: "Southern Copper Corporation", e: "NYSE", t: "Stock", c: "PE", i: "US84265V1052" },
    { s: "IFS", n: "Intercorp Financial Services", e: "NYSE", t: "Stock", c: "PE", i: "PAP494421009" },
    { s: "BVN", n: "Compañía de Minas Buenaventura", e: "NYSE", t: "Stock", c: "PE", i: "US2044481040" },
    { s: "ALICORC1", n: "Alicorp S.A.A.", e: "BVL", t: "Stock", c: "PE", i: "PEP214001005" },
    { s: "CPACASC1", n: "Cementos Pacasmayo S.A.A.", e: "BVL", t: "Stock", c: "PE", i: "PEP239501005" },
    { s: "FERREYC1", n: "Ferreycorp S.A.A.", e: "BVL", t: "Stock", c: "PE", i: "PEP314001004" },
    { s: "VOO", n: "Vanguard S&P 500 ETF (BVL)", e: "BVL", t: "ETF", c: "PE", i: "US9229083632" },
    { s: "AAPL", n: "Apple Inc.", e: "NASDAQ", t: "Stock", c: "US" },
  ],
  etf: [
    { s: "SPY", n: "SPDR S&P 500 ETF Trust", e: "NYSE ARCA", t: "ETF", c: "US" },
    { s: "QQQ", n: "Invesco QQQ Trust", e: "NASDAQ", t: "ETF", c: "US" },
    { s: "VOO", n: "Vanguard S&P 500 ETF", e: "NYSE ARCA", t: "ETF", c: "US" },
    { s: "GLD", n: "SPDR Gold Shares", e: "NYSE ARCA", t: "ETF", c: "US" },
  ],
  eu: [
    { s: "ASML", n: "ASML Holding N.V.", e: "EURONEXT", t: "Stock", c: "NL" },
    { s: "SAP", n: "SAP SE", e: "XETRA", t: "Stock", c: "DE" },
    { s: "SAN", n: "Banco Santander S.A.", e: "BME", t: "Stock", c: "ES" },
  ],
  asia: [
    { s: "7203", n: "Toyota Motor Corp", e: "TSE", t: "Stock", c: "JP" },
    { s: "005930", n: "Samsung Electronics", e: "KRX", t: "Stock", c: "KR" },
    { s: "9988", n: "Alibaba Group Holding", e: "HKEX", t: "Stock", c: "HK" },
  ],
};

const segmentCache = new Map<MarketSegment, MarketInstrumentRecord[]>();

/**
 * Carga un segmento de mercado desde public/data/market/ con caché en memoria.
 */
export async function loadMarketSegment(
  segment: MarketSegment,
  fetchImpl: typeof fetch = globalThis.fetch.bind(globalThis),
): Promise<MarketInstrumentRecord[]> {
  const cached = segmentCache.get(segment);
  if (cached && cached.length > 0) {
    return cached;
  }

  try {
    const url = `/data/market/market-${segment}.json`;
    const res = await fetchImpl(url);
    if (!res.ok) {
      // Degradar al seed básico de emergencia sin fallar
      const fallback = FALLBACK_SEEDS[segment] || [];
      segmentCache.set(segment, fallback);
      return fallback;
    }

    const data = (await res.json()) as MarketInstrumentRecord[];
    if (Array.isArray(data) && data.length > 0) {
      segmentCache.set(segment, data);
      return data;
    }
  } catch {
    // Si falla el fetch (ej. en tests sin servidor HTTP), usar fallback
  }

  const fallback = FALLBACK_SEEDS[segment] || [];
  segmentCache.set(segment, fallback);
  return fallback;
}

/**
 * Búsqueda de alta velocidad en cliente con priorización de ranking:
 * 1. Símbolo exacto
 * 2. Símbolo que comienza con el término
 * 3. Símbolo que contiene el término
 * 4. Nombre descriptivo que contiene el término
 * 5. ISIN coincidente
 */
export function searchMarketInstruments(
  records: readonly MarketInstrumentRecord[],
  rawQuery: string,
  limit = 50,
): MarketInstrumentRecord[] {
  const query = rawQuery.trim().toLowerCase();
  if (!query) {
    return records.slice(0, limit);
  }

  const exactSym: MarketInstrumentRecord[] = [];
  const startsSym: MarketInstrumentRecord[] = [];
  const containsSym: MarketInstrumentRecord[] = [];
  const containsName: MarketInstrumentRecord[] = [];
  const containsIsin: MarketInstrumentRecord[] = [];

  for (const item of records) {
    const sym = item.s.toLowerCase();
    const name = item.n.toLowerCase();
    const isin = (item.i || "").toLowerCase();

    if (sym === query) {
      exactSym.push(item);
    } else if (sym.startsWith(query)) {
      startsSym.push(item);
    } else if (sym.includes(query)) {
      containsSym.push(item);
    } else if (name.includes(query)) {
      containsName.push(item);
    } else if (isin && isin.includes(query)) {
      containsIsin.push(item);
    }

    // Cortar anticipadamente si ya acumulamos suficiente para el ranking
    if (exactSym.length + startsSym.length + containsSym.length >= limit * 2) {
      break;
    }
  }

  const merged = [
    ...exactSym,
    ...startsSym,
    ...containsSym,
    ...containsName,
    ...containsIsin,
  ];

  // Desduplicar preservando el orden de relevancia
  const seen = new Set<string>();
  const result: MarketInstrumentRecord[] = [];
  for (const item of merged) {
    if (!seen.has(item.s)) {
      seen.add(item.s);
      result.push(item);
      if (result.length >= limit) break;
    }
  }

  return result;
}
